import type { Product } from "../products/api";

export type ProductSortMode = "best-selling" | "alphabetical" | "category";

export const PRODUCT_SORT_LABELS: Record<ProductSortMode, string> = {
  "best-selling": "Más vendidos",
  alphabetical: "Alfabéticamente",
  category: "Por categoría",
};

// General apparel fallback for products without enough local history. Everyday
// tops, jeans/trousers and outerwear lead before seasonal items and accessories.
const BASELINE_POPULARITY = [
  "remera",
  "jean",
  "pantalon",
  "campera",
  "buzo",
  "camisa m/corta",
  "camisa m/larga",
  "boxer",
  "bermudas",
  "zapatillas",
  "malla",
  "gorra",
  "cinto",
  "perfume",
  "reloj",
  "cadena",
  "pulsera",
  "accesorio",
];

const popularityByName = new Map(
  BASELINE_POPULARITY.map((name, index) => [name, index])
);

function normalized(value: string) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLocaleLowerCase("es");
}

function alphabetical(a: Product, b: Product) {
  return a.name.localeCompare(b.name, "es", { sensitivity: "base" });
}

function baselineRank(product: Product) {
  return popularityByName.get(normalized(product.name)) ?? BASELINE_POPULARITY.length;
}

export function sortProducts(products: Product[], mode: ProductSortMode) {
  return [...products].sort((a, b) => {
    if (mode === "alphabetical") return alphabetical(a, b);
    if (mode === "category") {
      const categoryComparison = (a.category ?? "Sin categoría").localeCompare(
        b.category ?? "Sin categoría",
        "es",
        { sensitivity: "base" }
      );
      return categoryComparison || alphabetical(a, b);
    }

    const realSalesComparison = (b.unitsSold ?? 0) - (a.unitsSold ?? 0);
    return realSalesComparison || baselineRank(a) - baselineRank(b) || alphabetical(a, b);
  });
}
